import { Injectable } from '@angular/core';

import { deepEqual } from 'fast-equals';
import { shareReplay } from 'rxjs';

import { injectReportsTranslations } from '@core/reports-contracts';
import {
  ReportParamFilters,
  FilterGroup,
  ReportsComponentTileFilterBreadcrumbSync,
  ReportsFilterBreadcrumb,
  BreadcrumbsService,
  BreadcrumbsServiceSymbol,
  BreadcrumbsParams,
} from '@core/reports-types';
import { getAvailableFilters } from '@core/reports-util';

type ComponentFiltersMap = Record<string, string[]>;

@Injectable()
export class AppliedFiltersBreadcrumbsService implements BreadcrumbsService<'applied'> {
  public [BreadcrumbsServiceSymbol] = <const>'applied';
  private reportsTranslations = injectReportsTranslations();

  public getBreadcrumbsSync(params: BreadcrumbsParams): ReportsComponentTileFilterBreadcrumbSync[] {
    return this.breadcrumbs(params, true);
  }

  public getBreadcrumbs(params: BreadcrumbsParams): ReportsFilterBreadcrumb[] {
    return this.breadcrumbs(params, false);
  }

  private breadcrumbs(
    params: BreadcrumbsParams,
    sync: true,
  ): ReportsComponentTileFilterBreadcrumbSync[];
  private breadcrumbs(params: BreadcrumbsParams, sync: false): ReportsFilterBreadcrumb[];
  private breadcrumbs(params: BreadcrumbsParams, sync: boolean): ReportsFilterBreadcrumb[] {
    const { data, metric, componentSavedFilters } = params;

    // dashboard + component
    const appliedFilterGroupsMap = this.getFiltersMap(data.filters);
    const appliedFilterGroups = <FilterGroup[]>Object.keys(appliedFilterGroupsMap);

    // component only
    let overwritten: Record<string, string[]> = {};

    if (componentSavedFilters) {
      const componentFilterGroupsMap = this.getFiltersMap(componentSavedFilters.filters);

      overwritten = this.determineOverwrites(appliedFilterGroupsMap, componentFilterGroupsMap);
    }

    const sharedBreadcrumbData = (group: FilterGroup) => ({
      simple: <const>false,
      invalid: !getAvailableFilters(data, metric).find(
        ({ id, parameterized }) => id === group || (parameterized && group.startsWith(id + ':')),
      ),
      group,
      translatedGroup: this.reportsTranslations.translateFilterGroup(group),
      hasOverwrites: Array.isArray(overwritten[group]),
    });

    // sync returns plain values
    if (sync) {
      return appliedFilterGroups
        .map(group => ({
          ...sharedBreadcrumbData(group),
          // sync returns null if translation is not found
          translatedValues: (appliedFilterGroupsMap[group] || []).map(value =>
            this.reportsTranslations.translateFilterSync(group, value),
          ),
          // important line to filter null results from dictionary sync result
        }))
        .filter(x => x.translatedValues.every(x => x !== null));
    }
    // non sync returns observable values
    return appliedFilterGroups.map(group => ({
      ...sharedBreadcrumbData(group),
      translatedValues: (appliedFilterGroupsMap[group] || []).map(value =>
        this.reportsTranslations.translateFilter(group, value).pipe(shareReplay(1)),
      ),
    }));
  }

  private getFiltersMap(filters: ReportParamFilters) {
    return filters.reduce(
      (acc, filter) => {
        if (acc[filter.group]) {
          acc[filter.group].push(filter.value);
        } else {
          acc[filter.group] = [filter.value];
        }

        return acc;
      },
      <ComponentFiltersMap>{},
    );
  }

  private determineOverwrites(
    original: Record<string, string[]>,
    applied: Record<string, string[]>,
  ) {
    const overwrites: Record<string, string[]> = {};

    for (const group in applied) {
      if (!deepEqual(applied[group], original[group])) overwrites[group] = applied[group];
    }

    return overwrites;
  }
}
